import React, { useState } from 'react';
import { useT } from '../BuilderApp.jsx';
import WizardShell from '../WizardShell.jsx';
import { CheckIcon } from '../../components/ui/Icons.jsx';
import { TEMPLATE_CATEGORIES } from '../../data/templates.js';
import { tmplById } from '../../data/defaults.js';

function StepNextHint({ step }) {
  const t = useT();
  const msg = t[`next${step}`];
  if (!msg) return null;
  return (
    <div style={{display:'flex',alignItems:'center',gap:'.4rem',justifyContent:'center',marginTop:'.55rem',fontSize:'.72rem',color:'rgba(148,163,184,.55)',letterSpacing:'.01em',lineHeight:1.4}}>
      <svg width="11" height="11" viewBox="0 0 11 11" fill="none" style={{flexShrink:0,opacity:.55}}>
        <path d="M2 5.5h7M6 2.5l3 3-3 3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
      <span>{msg}</span>
    </div>
  );
}

export default function Step2({ config, setField, next, back, lang, setLang }) {
  const t = useT();
  const no = ['no', 'sv', 'da'].includes(lang);
  const [selectedCat, setSelectedCat] = useState(config.template_category || null);
  const [hoveredSub, setHoveredSub] = useState(null);

  const activeCat = TEMPLATE_CATEGORIES.find(c => c.id === selectedCat);

  const pickSub = (sub) => {
    setField('template', sub.id);
    setField('template_category', selectedCat);
  };

  // Category picker view
  if (!selectedCat) {
    return (
      <WizardShell step={2} total={8} title={t.s2_cat_title} subtitle={t.s2_cat_sub} onBack={back} lang={lang} setLang={setLang}>
        {/* Tip */}
        <div style={{background:'rgba(59,130,246,.07)',border:'1px solid rgba(59,130,246,.18)',borderRadius:14,padding:'.85rem 1rem',marginBottom:'1.25rem',display:'flex',gap:'.65rem',alignItems:'flex-start'}}>
          <span style={{fontSize:'1.1rem',flexShrink:0,marginTop:'.05rem'}}>💡</span>
          <p style={{fontSize:'.8rem',color:'var(--muted)',lineHeight:1.6}}>{t.s2_tip}</p>
        </div>

        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(min(100%,280px),1fr))',gap:'.85rem'}}>
          {TEMPLATE_CATEGORIES.map(cat => (
            <button
              key={cat.id}
              onClick={() => setSelectedCat(cat.id)}
              style={{
                background:'rgba(255,255,255,.04)',
                border:`1.5px solid ${config.template_category === cat.id ? cat.accent : 'rgba(255,255,255,.12)'}`,
                borderRadius:16,padding:'1.25rem',cursor:'pointer',textAlign:'left',
                transition:'all .2s',
                boxShadow: config.template_category === cat.id ? `0 0 0 1px ${cat.accent}55,0 4px 24px ${cat.accent}22` : 'none',
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = cat.accent; e.currentTarget.style.background = 'rgba(255,255,255,.07)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = config.template_category === cat.id ? cat.accent : 'rgba(255,255,255,.12)'; e.currentTarget.style.background = 'rgba(255,255,255,.04)'; }}
            >
              <div style={{display:'flex',alignItems:'center',gap:'.75rem',marginBottom:'.75rem'}}>
                <div style={{width:44,height:44,borderRadius:12,background:`${cat.accent}22`,border:`1px solid ${cat.accent}44`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:'1.35rem',flexShrink:0}}>
                  {cat.icon}
                </div>
                <div>
                  <div style={{fontWeight:800,fontSize:'.95rem',marginBottom:'.1rem'}}>
                    {no && cat.label_no ? cat.label_no : cat.label}
                  </div>
                  <div style={{fontSize:'.72rem',color:'var(--muted)'}}>
                    {no && cat.sub_no ? cat.sub_no : cat.sub}
                  </div>
                </div>
              </div>
              {/* Mini sub-type chips */}
              <div style={{display:'flex',flexWrap:'wrap',gap:'.3rem'}}>
                {cat.templates.map(sub => (
                  <span key={sub.id} style={{fontSize:'.65rem',background:'rgba(255,255,255,.07)',border:'1px solid rgba(255,255,255,.1)',borderRadius:6,padding:'.2rem .45rem',color:'var(--muted)'}}>
                    {sub.icon} {no && sub.label_no ? sub.label_no : sub.label}
                  </span>
                ))}
              </div>
              <div style={{marginTop:'.85rem',fontSize:'.72rem',color:cat.accent,fontWeight:700,display:'flex',alignItems:'center',gap:'.25rem'}}>
                {no ? 'Se maler' : 'See templates'} →
              </div>
            </button>
          ))}
        </div>
      </WizardShell>
    );
  }

  // Sub-template picker view
  return (
    <WizardShell
      step={2} total={8}
      title={no && activeCat.label_no ? activeCat.label_no : activeCat.label}
      subtitle={t.s2_tmpl_sub}
      onBack={() => setSelectedCat(null)}
      lang={lang} setLang={setLang}
    >
      {/* Back to categories chip */}
      <button
        onClick={() => setSelectedCat(null)}
        style={{display:'inline-flex',alignItems:'center',gap:'.35rem',background:'rgba(255,255,255,.06)',border:'1px solid var(--border2)',borderRadius:8,padding:'.35rem .75rem',fontSize:'.75rem',color:'var(--muted)',cursor:'pointer',marginBottom:'1rem',fontFamily:'inherit'}}
      >
        ← {t.s2_tmpl_back || 'All categories'}
      </button>

      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(min(100%,260px),1fr))',gap:'.85rem'}}>
        {activeCat.templates.map(sub => {
          const isSelected = config.template === sub.id;
          const isHovered = hoveredSub === sub.id;
          const baseT = tmplById(sub.id);
          return (
            <div
              key={sub.id}
              className={`radio-card${isSelected ? ' selected' : ''}`}
              onClick={() => pickSub(sub)}
              onMouseEnter={() => setHoveredSub(sub.id)}
              onMouseLeave={() => setHoveredSub(null)}
              style={{cursor:'pointer'}}
            >
              {/* Photo */}
              <div style={{position:'relative',height:160,overflow:'hidden',background:'#0c1a2e'}}>
                <img
                  src={sub.photoUrl || baseT.photoUrl || 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?auto=format&fit=crop&w=600&q=80'}
                  alt={sub.label}
                  style={{width:'100%',height:'100%',objectFit:'cover',transition:'transform .3s',transform:isHovered ? 'scale(1.04)' : 'scale(1)'}}
                />
                <div style={{position:'absolute',inset:0,background:`linear-gradient(180deg,transparent 40%,${sub.accent}44 100%)`}}/>
                <div style={{position:'absolute',top:'.6rem',left:'.6rem',background:'rgba(5,10,20,.85)',backdropFilter:'blur(8px)',border:'1px solid rgba(255,255,255,.12)',borderRadius:8,padding:'.3rem .55rem',fontSize:'.8rem',fontWeight:700}}>
                  {sub.icon}
                </div>
                {isSelected && (
                  <div style={{position:'absolute',inset:0,border:'2px solid var(--blue)',borderRadius:'inherit',pointerEvents:'none'}}>
                    <div style={{position:'absolute',top:'.5rem',right:'.5rem',width:22,height:22,borderRadius:'50%',background:'var(--blue)',display:'flex',alignItems:'center',justifyContent:'center',boxShadow:'0 2px 8px rgba(37,99,235,.5)'}}>
                      <CheckIcon size={11} color="#fff"/>
                    </div>
                  </div>
                )}
              </div>
              {/* Info */}
              <div style={{padding:'.8rem',borderTop:'1px solid rgba(255,255,255,.08)'}}>
                <div style={{display:'flex',alignItems:'center',gap:'.4rem',marginBottom:'.3rem'}}>
                  <div style={{width:8,height:8,borderRadius:'50%',background:sub.accent,flexShrink:0}}/>
                  <span style={{fontWeight:700,fontSize:'.88rem'}}>
                    {no && sub.label_no ? sub.label_no : sub.label}
                  </span>
                </div>
                <p style={{fontSize:'.73rem',color:'var(--muted)',lineHeight:1.45}}>
                  {no && sub.sub_no ? sub.sub_no : sub.sub}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Info bar */}
      <div style={{marginTop:'1rem',background:'rgba(255,255,255,.03)',border:'1px solid var(--border)',borderRadius:12,padding:'.7rem 1rem',display:'flex',gap:'.6rem',alignItems:'center'}}>
        <span style={{fontSize:'.85rem'}}>✨</span>
        <p style={{fontSize:'.75rem',color:'var(--muted)',lineHeight:1.5}}>{t.s2_tmpl_note}</p>
      </div>

      <button
        className="btn btn-primary btn-lg btn-full"
        onClick={next}
        disabled={!config.template}
        style={{marginTop:'1.25rem'}}
      >
        {t.s2_btn}
      </button>
      <StepNextHint step={2}/>
    </WizardShell>
  );
}
